import { Link, useLocation } from 'react-router-dom';

/**
 * AdminBreadcrumb
 * 
 * Breadcrumb navigation component for admin pages.
 * Shows: Admin / <Section> / <Page>
 */
export default function AdminBreadcrumb({ items = [] }) {
  const location = useLocation();

  // Auto-generate breadcrumb from path if items not provided
  const breadcrumbItems = items.length > 0 ? items : generateBreadcrumbFromPath(location.pathname);

  if (breadcrumbItems.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" style={styles.container}>
      <ol style={styles.list}>
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          return (
            <li key={item.path || index} style={styles.item}>
              {isLast ? (
                <span style={styles.current} aria-current="page">
                  {item.label}
                </span>
              ) : (
                <>
                  <Link to={item.path} style={styles.link}>
                    {item.label}
                  </Link>
                  <span style={styles.separator} aria-hidden="true">
                    /
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function generateBreadcrumbFromPath(pathname) {
  const items = [{ path: '/admin', label: 'Admin' }];

  // Remove /admin prefix
  const path = pathname.replace(/^\/admin\/?/, '');
  if (!path) return items;

  // Split path into segments
  const segments = path.split('/').filter(Boolean);

  // Map segments to labels
  const segmentLabels = {
    'ops': 'Ops',
    'notifications': 'Notifications',
    'api-controller': 'Integrations',
    'audit': 'Audit',
    'data': 'Data',
    'owners': 'Owners',
    'properties': 'Properties',
    'data-quality': 'Data Quality',
    'imports': 'Imports',
    'users': 'Users',
    'roles': 'Roles',
    'api-keys': 'API Keys',
    'integrations': 'Integration',
  };

  let currentPath = '/admin';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const label = segmentLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
    
    // For detail pages, show ID or code
    if (index === segments.length - 1 && (segment.match(/^[a-f0-9-]{8,}$/i) || segment.length < 20)) {
      items.push({ path: currentPath, label: segment.substring(0, 8) + '...' });
    } else {
      items.push({ path: currentPath, label });
    }
  });

  return items;
}

const styles = {
  container: {
    marginBottom: 'var(--spacing-2)', // 16px
  },
  list: {
    display: 'flex',
    alignItems: 'center',
    listStyle: 'none',
    padding: 0,
    margin: 0,
    flexWrap: 'wrap',
    gap: 'var(--spacing-1)', // 8px
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1)', // 8px
  },
  link: {
    color: 'var(--color-brand-primary)',
    textDecoration: 'none',
    fontSize: 'var(--font-size-sm)', // 14px
    transition: 'color var(--transition-base)',
  },
  current: {
    color: 'var(--color-text-primary)',
    fontSize: 'var(--font-size-sm)', // 14px
    fontWeight: 'var(--font-weight-medium)',
  },
  separator: {
    color: 'var(--color-text-tertiary)',
    fontSize: 'var(--font-size-sm)', // 14px
  },
};
